import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  effect,
  inject,
  signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { SplitViewCollapse, SplitViewTriggerCollapse } from '../../interfaces';
import { UiHelpersService } from '../../services';

@Component({
  selector: 'phoenix-split-pane',
  templateUrl: './split-pane.component.html',
  styleUrls: ['./split-pane.component.scss'],
  standalone: false,
})
export class SplitPaneComponent implements OnInit, OnChanges {
  uiHelper = inject(UiHelpersService);
  dialog = inject(MatDialog);
  @ViewChild('container', { static: true }) containerRef!: ElementRef;

  @Input() leftWidth: number = 50;
  @Input() rightWidth: number = 50;
  @Input() dragEnabled: boolean = false;
  @Input() minWidth: number = 10;
  @Input() collapsable = true;
  @Input() collapsableHeight = 50;
  @Input() escapeCollapseDirection: SplitViewCollapse = 'none';
  @Input() triggerCollapse: SplitViewTriggerCollapse = null;
  @Output() triggerCollapseChange =
    new EventEmitter<SplitViewTriggerCollapse>();

  @Output() splitChange = new EventEmitter<SplitViewCollapse>();

  @Output() resizeEnd = new EventEmitter<void>(); // Emitted when the pane resize animation (flex-basis) has finished.

  leftPaneWidth = signal<number>(50);
  rightPaneWidth = signal<number>(50);
  dragging = false;
  startX: number | null = null;
  startLeftPaneWidth: number | null = null;
  containerWidth: number | null = null;

  buttonAnimating = false;
  collapsed = signal<SplitViewCollapse>('none');

  get leftHeight() {
    return this.collapsableHeight + 'px';
  }

  get rightHeight() {
    return this.collapsableHeight + 30 + 'px';
  }

  constructor() {
    effect(() => {
      this.splitChange.emit(this.collapsed());
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['triggerCollapse']) {
      if (this.triggerCollapse !== this.collapsed() && this.triggerCollapse) {
        this.toggleCollapse(this.triggerCollapse);
      }
      setTimeout(() => {
        this.triggerCollapse = null;
        this.triggerCollapseChange.emit(this.triggerCollapse);
      });
    }
    if (changes['collapsable']) {
      this.calculateCollpaseWidths();
    }
  }

  // Fires when a pane's width animation completes.
  onResizeTransitionEnd(event: TransitionEvent): void {
    if (event.target !== event.currentTarget) {
      return;
    }
    if (event.propertyName === 'flex-basis' && !this.dragging) {
      this.resizeEnd.emit();
    }
  }

  calculateCollpaseWidths() {
    if (this.collapsable) {
      this.calculatePaneWidths(this.leftWidth, this.rightWidth);
    } else {
      this.leftPaneWidth.set(99);
      this.rightPaneWidth.set(0);
      this.collapsed.set('right');
    }
  }

  ngOnInit(): void {
    this.calculateCollpaseWidths();
  }

  calculatePaneWidths(leftWidth: number, rightWidth: number) {
    const totalWidth = leftWidth + rightWidth;
    this.leftPaneWidth.set((leftWidth / totalWidth) * 100);
    this.rightPaneWidth.set((rightWidth / totalWidth) * 100);
    this.collapsed.set('none');
  }

  startDragging(event: MouseEvent): void {
    if (
      !this.dragEnabled ||
      this.collapsed() !== 'none' ||
      this.buttonAnimating
    ) {
      return;
    }
    this.dragging = true;
    this.startX = event.clientX;
    this.startLeftPaneWidth = this.leftPaneWidth();

    this.containerWidth = this.containerRef.nativeElement.clientWidth;

    event.preventDefault();
  }

  @HostListener('window:mousemove', ['$event'])
  onDragging(event: MouseEvent): void {
    if (
      !this.dragging ||
      this.startX === null ||
      this.containerWidth === null ||
      this.startLeftPaneWidth === null ||
      this.collapsed() !== 'none' ||
      this.buttonAnimating
    ) {
      return;
    }

    const diffX = event.clientX - this.startX;

    const newLeftPaneWidth =
      (((this.startLeftPaneWidth * this.containerWidth) / 100 + diffX) /
        this.containerWidth) *
      100;

    if (
      newLeftPaneWidth > this.minWidth &&
      newLeftPaneWidth < 100 - this.minWidth
    ) {
      this.leftPaneWidth.set(newLeftPaneWidth);
      this.rightPaneWidth.set(100 - newLeftPaneWidth);
    }
  }

  @HostListener('window:mouseup')
  stopDragging(): void {
    this.dragging = false;
    this.startX = null;
    this.containerWidth = null;
    this.startLeftPaneWidth = null;
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardShortcut(event: KeyboardEvent): void {
    // Check if Shift+Escape key was pressed and we have a direction set
    if (
      event.key === 'Escape' &&
      event.shiftKey &&
      this.escapeCollapseDirection !== 'none' &&
      !this.buttonAnimating &&
      this.collapsable &&
      this.dialog.openDialogs.length === 0 &&
      !this.uiHelper.isInputFocused(event.target as HTMLElement)
    ) {
      // If the pane is already collapsed in the specified direction, expand it
      // Otherwise collapse in the specified direction
      this.toggleCollapse(this.escapeCollapseDirection);
      event.preventDefault();
    }
  }

  toggleCollapse(direction: SplitViewCollapse): void {
    this.buttonAnimating = true;

    // First hide the button with animation

    if (this.collapsed() === direction || direction === 'none') {
      // Transitioning from collapsed state to normal state
      this.leftPaneWidth.set(this.leftWidth);
      this.rightPaneWidth.set(this.rightWidth);
      this.collapsed.set('none');
    } else if (this.collapsed() === 'none') {
      // Transitioning from normal state to collapsed state
      if (direction === 'left') {
        this.leftPaneWidth.set(0);
        this.rightPaneWidth.set(99);
      } else {
        this.leftPaneWidth.set(99);
        this.rightPaneWidth.set(0);
      }
      this.collapsed.set(direction);
    } else {
      // Transitioning from one collapsed state to normal state
      this.leftPaneWidth.set(this.leftWidth);
      this.rightPaneWidth.set(this.rightWidth);
      this.collapsed.set('none');
    }

    // Allow time for the panel animation to complete before showing the button
    setTimeout(() => {
      this.buttonAnimating = false;
    }, 200); // Duration to wait before showing the button again
  }
}
